import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { FiChevronLeft, FiChevronRight, FiClock, FiCheck, FiMail, FiPhone, FiUser, FiStar, FiMinus, FiInfo, FiCamera, FiHome, FiImage } from 'react-icons/fi';
import { Link } from 'react-router-dom';
import toast from 'react-hot-toast';
import { availabilityAPI, bookingsAPI, packagesAPI, addonsAPI } from '../utils/api';
import SEO from '../components/SEO';
import './Booking.css';

const Booking = () => {
  const [currentMonth, setCurrentMonth] = useState(new Date());
  const [selectedDate, setSelectedDate] = useState(null);
  const [selectedTime, setSelectedTime] = useState(null);
  const [selectedPackage, setSelectedPackage] = useState(null);
  const [includeStudio, setIncludeStudio] = useState(false);
  const [selectedAddons, setSelectedAddons] = useState([]);
  const [availability, setAvailability] = useState([]);
  const [bookedSlots, setBookedSlots] = useState([]); // Time slots already booked (confirmed)
  const [loadingSlots, setLoadingSlots] = useState(false);
  const [sessionPackages, setSessionPackages] = useState([]);
  const [bookingAddons, setBookingAddons] = useState([]);
  const [loading, setLoading] = useState(true);
  const timeSlots = ['09:00', '10:00', '11:00', '13:00', '14:00', '15:00', '16:00', '17:00'];
  const [step, setStep] = useState(1);
  
  // Helper to change step (scroll is handled in useEffect so it runs after re-render on mobile too)
  const goToStep = useCallback((newStep) => {
    setStep(newStep);
  }, []);

  // Scroll to top when step changes – after paint so mobile gets correct scroll (fixes "end of page" on mobile)
  useEffect(() => {
    const scrollToTop = () => {
      window.scrollTo({ top: 0, behavior: 'smooth' });
      document.documentElement.scrollTop = 0;
      document.body.scrollTop = 0;
    };
    // Defer until after React has rendered the new step content (fixes mobile)
    const id = requestAnimationFrame(() => {
      requestAnimationFrame(scrollToTop);
    });
    // Backup for slower mobile devices so scroll happens after layout
    const timeoutId = setTimeout(scrollToTop, 150);
    return () => {
      cancelAnimationFrame(id);
      clearTimeout(timeoutId);
    };
  }, [step]);
  const [submitting, setSubmitting] = useState(false);
  const [bookingSuccess, setBookingSuccess] = useState(false);
  const [submittedBooking, setSubmittedBooking] = useState(null);
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    message: ''
  });

  // Helper to get discounted price (null = depends on project)
  const getDiscountedPrice = (pkg) => {
    if (!pkg) return null;
    if (pkg.price == null || pkg.price === '') return null;
    if (pkg.discountPercent > 0) {
      return Math.round(pkg.price * (1 - pkg.discountPercent / 100));
    }
    return pkg.price;
  };

  const hasPackagePrice = (pkg) => pkg && (pkg.price != null && pkg.price > 0);

  // Calculate total price dynamically (null when session has no price and no extras)
  const totalPrice = useMemo(() => {
    if (!selectedPackage) return 0;
    const sessionPrice = getDiscountedPrice(selectedPackage) ?? 0;
    let total = sessionPrice;
    if (includeStudio) total += selectedPackage.studioRentalPrice ?? 0;
    selectedAddons.forEach(addonId => {
      const addon = bookingAddons.find(a => a._id === addonId);
      if (addon) total += addon.price;
    });
    return total;
  }, [selectedPackage, includeStudio, selectedAddons, bookingAddons]);

  const hasSessionPrice = (pkg) => pkg && pkg.price != null && pkg.price !== '';

  // Price breakdown for summary (price: null = "depends on project")
  const priceBreakdown = useMemo(() => {
    if (!selectedPackage) return [];
    const discountedPrice = getDiscountedPrice(selectedPackage);
    const breakdown = [
      { 
        label: selectedPackage.name + (selectedPackage.discountPercent > 0 && hasSessionPrice(selectedPackage) ? ` (-${selectedPackage.discountPercent}%)` : ''), 
        price: discountedPrice,
        dependsOnProject: !hasSessionPrice(selectedPackage)
      }
    ];
    if (includeStudio) {
      breakdown.push({ label: 'Studio Rental', price: selectedPackage.studioRentalPrice });
    }
    selectedAddons.forEach(addonId => {
      const addon = bookingAddons.find(a => a._id === addonId);
      if (addon) breakdown.push({ label: addon.name, price: addon.price });
    });
    return breakdown;
  }, [selectedPackage, includeStudio, selectedAddons, bookingAddons]);

  // Load packages and addons on mount
  useEffect(() => {
    loadPackagesAndAddons();
  }, []);

  const loadPackagesAndAddons = async () => {
    setLoading(true);
    try {
      const [packagesRes, addonsRes] = await Promise.all([
        packagesAPI.getAll(true).catch(() => ({ packages: [] })),
        addonsAPI.getAll(true).catch(() => ({ addons: [] }))
      ]);
      setSessionPackages(packagesRes.packages || []);
      setBookingAddons(addonsRes.addons || []);
    } catch (error) {
      console.error('Failed to load booking data:', error);
    }
    setLoading(false);
  };

  const loadAvailability = useCallback(async () => {
    try {
      const month = currentMonth.getMonth() + 1;
      const year = currentMonth.getFullYear();
      const res = await availabilityAPI.getAll(month, year);
      setAvailability(res.availability || []);
    } catch (error) {
      console.error('Failed to load availability:', error);
      setAvailability([]);
    }
  }, [currentMonth]);

  // Load availability when month changes
  useEffect(() => {
    loadAvailability();
  }, [loadAvailability]);

  // Toggle addon selection
  const toggleAddon = (addonId) => {
    setSelectedAddons(prev => 
      prev.includes(addonId) 
        ? prev.filter(id => id !== addonId)
        : [...prev, addonId]
    );
  };

  // Loading state
  if (loading) {
    return (
      <div className="booking-page">
        <SEO title="Book Session" description="Book a photography or videography session with Prague photographer Marta Zborovska. Portraits, weddings, fashion. Prague studio." path="/booking" />
        <div className="booking-header">
          <h1>Book Your Session</h1>
          <p>Loading session options...</p>
        </div>
      </div>
    );
  }

  // Calendar helpers
  const getDaysInMonth = (date) => {
    const year = date.getFullYear();
    const month = date.getMonth();
    const firstDay = new Date(year, month, 1);
    const lastDay = new Date(year, month + 1, 0);
    const daysInMonth = lastDay.getDate();
    const startingDay = firstDay.getDay();
    return { daysInMonth, startingDay };
  };

  // Use local date to avoid timezone shift (toISOString converts to UTC, can shift day)
  const formatDate = (date) => {
    const y = date.getFullYear();
    const m = String(date.getMonth() + 1).padStart(2, '0');
    const d = String(date.getDate()).padStart(2, '0');
    return `${y}-${m}-${d}`;
  };

  const isDateAvailable = (day) => {
    const date = new Date(currentMonth.getFullYear(), currentMonth.getMonth(), day);
    const dateStr = formatDate(date);
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    
    if (date < today) return false;
    
    const blocked = availability.find(a => a.date === dateStr && !a.available);
    if (blocked) return false;
    
    return true;
  };

  const isDateBlocked = (day) => {
    const date = new Date(currentMonth.getFullYear(), currentMonth.getMonth(), day);
    const dateStr = formatDate(date);
    return availability.some(d => d.date === dateStr && !d.available);
  };

  const prevMonth = () => {
    setCurrentMonth(new Date(currentMonth.getFullYear(), currentMonth.getMonth() - 1, 1));
  };

  const nextMonth = () => {
    setCurrentMonth(new Date(currentMonth.getFullYear(), currentMonth.getMonth() + 1, 1));
  };

  const selectDate = async (day) => {
    if (!isDateAvailable(day) || isDateBlocked(day)) return;
    const date = new Date(currentMonth.getFullYear(), currentMonth.getMonth(), day);
    setSelectedDate(date);
    setSelectedTime(null);
    setBookedSlots([]);
    
    // Fetch booked time slots for this date
    setLoadingSlots(true);
    try {
      const dateStr = formatDate(date);
      const res = await bookingsAPI.getBookedSlots(dateStr);
      setBookedSlots(res.bookedSlots || []);
    } catch (error) {
      console.error('Failed to load booked slots:', error);
      setBookedSlots([]);
    }
    setLoadingSlots(false);
  };
  
  // Check if a time slot is already booked
  const isTimeSlotBooked = (time) => {
    return bookedSlots.includes(time);
  };

  const handleFormChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    
    try {
      const booking = {
        packageId: selectedPackage._id,
        packageName: selectedPackage.name,
        packageDuration: selectedPackage.duration,
        packagePrice: getDiscountedPrice(selectedPackage),
        discountPercent: selectedPackage.discountPercent || 0,
        originalPrice: selectedPackage.discountPercent > 0 ? selectedPackage.price : null,
        studioRental: includeStudio,
        studioPrice: includeStudio ? selectedPackage.studioRentalPrice : 0,
        addons: selectedAddons.map(addonId => {
          const addon = bookingAddons.find(a => a._id === addonId);
          return addon ? { id: addon._id, name: addon.name, price: addon.price } : null;
        }).filter(Boolean),
        totalPrice: totalPrice,
        date: formatDate(selectedDate),
        time: selectedTime,
        customer: formData
      };

      await bookingsAPI.create(booking);
      
      // Store submitted booking details for success page
      setSubmittedBooking({
        packageName: selectedPackage.name,
        date: selectedDate.toLocaleDateString('en-US', { 
          weekday: 'long', 
          year: 'numeric', 
          month: 'long', 
          day: 'numeric' 
        }),
        time: selectedTime,
        customerName: formData.name,
        customerEmail: formData.email
      });
      setBookingSuccess(true);
      
    } catch (error) {
      toast.error(error.message || 'Failed to submit booking. Please try again.');
    }
    
    setSubmitting(false);
  };
  
  // Reset and start new booking
  const handleNewBooking = () => {
    setBookingSuccess(false);
    setSubmittedBooking(null);
    setStep(1);
    setSelectedDate(null);
    setSelectedTime(null);
    setSelectedPackage(null);
    setIncludeStudio(false);
    setSelectedAddons([]);
    setFormData({ name: '', email: '', phone: '', message: '' });
  };

  const renderCalendar = () => {
    const { daysInMonth, startingDay } = getDaysInMonth(currentMonth);
    const days = [];
    const monthNames = ['January', 'February', 'March', 'April', 'May', 'June', 
                        'July', 'August', 'September', 'October', 'November', 'December'];

    for (let i = 0; i < startingDay; i++) {
      days.push(<div key={`empty-${i}`} className="calendar-day empty"></div>);
    }

    for (let day = 1; day <= daysInMonth; day++) {
      const available = isDateAvailable(day);
      const blocked = isDateBlocked(day);
      const selected = selectedDate && 
        selectedDate.getDate() === day && 
        selectedDate.getMonth() === currentMonth.getMonth() &&
        selectedDate.getFullYear() === currentMonth.getFullYear();

      days.push(
        <div
          key={day}
          className={`calendar-day ${available && !blocked ? 'available' : 'unavailable'} ${selected ? 'selected' : ''}`}
          onClick={() => selectDate(day)}
        >
          {day}
        </div>
      );
    }

    return (
      <div className="calendar">
        <div className="calendar-header">
          <button onClick={prevMonth} className="calendar-nav"><FiChevronLeft /></button>
          <h3>{monthNames[currentMonth.getMonth()]} {currentMonth.getFullYear()}</h3>
          <button onClick={nextMonth} className="calendar-nav"><FiChevronRight /></button>
        </div>
        <div className="calendar-weekdays">
          {['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'].map(day => (
            <div key={day} className="weekday">{day}</div>
          ))}
        </div>
        <div className="calendar-days">
          {days}
        </div>
      </div>
    );
  };

  // Floating price summary: click scrolls to step actions and focuses primary button
  const focusStepButton = () => {
    const actions = document.getElementById('step-actions');
    if (actions) {
      actions.scrollIntoView({ behavior: 'smooth', block: 'center' });
      const primaryBtn = actions.querySelector('.next-btn, .submit-btn');
      if (primaryBtn) setTimeout(() => primaryBtn.focus(), 400);
    }
  };

  const PriceSummaryFloating = () => {
    if (!selectedPackage) return null;
    
    return (
      <div 
        className="price-summary-floating" 
        onClick={focusStepButton}
        onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); focusStepButton(); } }}
        role="button"
        tabIndex={0}
        aria-label="Go to continue button"
      >
        <div className="price-summary-header">
          <FiCamera />
          <span>Your Session</span>
        </div>
        <div className="price-summary-total">
          <span>{!hasPackagePrice(selectedPackage) && totalPrice === 0 ? 'Depends on project' : `${totalPrice.toLocaleString()} CZK`}</span>
        </div>
      </div>
    );
  };

  // Success Page
  if (bookingSuccess && submittedBooking) {
    return (
      <div className="booking-page">
        <SEO title="Booking Confirmed" description="Your photography session with Prague photographer Marta Zborovska has been booked. Confirmation sent." path="/booking" />
        <div className="booking-success">
          <div className="success-icon">
            <FiCheck />
          </div>
          <h1>Booking Request Sent!</h1>
          <p className="success-message">
            Thank you, <strong>{submittedBooking.customerName}</strong>!<br />
            Your booking request has been received.
          </p>
          
          <div className="success-details">
            <div className="success-detail-item">
              <span className="label">Session</span>
              <span className="value">{submittedBooking.packageName}</span>
            </div>
            <div className="success-detail-item">
              <span className="label">Date</span>
              <span className="value">{submittedBooking.date}</span>
            </div>
            <div className="success-detail-item">
              <span className="label">Time</span>
              <span className="value">{submittedBooking.time}</span>
            </div>
          </div>
          
          <div className="success-info">
            <FiMail />
            <p>
              A confirmation email has been sent to <strong>{submittedBooking.customerEmail}</strong>.<br />
              I'll review your request and get back to you within 24-48 hours.
            </p>
          </div>
          
          <div className="success-actions">
            <Link to="/portfolio" className="success-btn primary">
              <FiImage /> View Portfolio
            </Link>
            <button onClick={handleNewBooking} className="success-btn secondary">
              Book Another Session
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="booking-page">
      <SEO
        title="Book Photography Session"
        description="Book your photo session with Prague photographer Marta Zborovska. Portrait, wedding, fashion photography in Prague. Studio rental available."
        path="/booking"
      />
      <div className="booking-header">
        <h1>Book Your Session</h1>
        <p>Professional photography tailored to your needs</p>
      </div>

      <div className="booking-steps">
        <div className={`step ${step >= 1 ? 'active' : ''} ${step > 1 ? 'completed' : ''}`}>
          <span className="step-number">{step > 1 ? <FiCheck /> : '1'}</span>
          <span className="step-label">Package</span>
        </div>
        <div className="step-connector"></div>
        <div className={`step ${step >= 2 ? 'active' : ''} ${step > 2 ? 'completed' : ''}`}>
          <span className="step-number">{step > 2 ? <FiCheck /> : '2'}</span>
          <span className="step-label">Customize</span>
        </div>
        <div className="step-connector"></div>
        <div className={`step ${step >= 3 ? 'active' : ''} ${step > 3 ? 'completed' : ''}`}>
          <span className="step-number">{step > 3 ? <FiCheck /> : '3'}</span>
          <span className="step-label">Date & Time</span>
        </div>
        <div className="step-connector"></div>
        <div className={`step ${step >= 4 ? 'active' : ''}`}>
          <span className="step-number">4</span>
          <span className="step-label">Details</span>
        </div>
      </div>

      {selectedPackage && <PriceSummaryFloating />}

      <div className="booking-content">
        {/* Step 1: Select Package */}
        {step === 1 && (
          <div className="step-content">
            <h2>Choose Your Session</h2>
            <p className="step-description">Select the package that best fits your needs</p>
            
            <div className="packages-grid">
              {sessionPackages.map(pkg => (
                <div
                  key={pkg._id}
                  className={`package-card ${selectedPackage?._id === pkg._id ? 'selected' : ''} ${pkg.recommended ? 'recommended' : ''} ${pkg.discountPercent > 0 ? 'has-discount' : ''}`}
                  onClick={() => setSelectedPackage(pkg)}
                >
                  {pkg.recommended && (
                    <div className="recommended-badge">
                      <FiStar /> Recommended
                    </div>
                  )}
                  
                  {pkg.discountPercent > 0 && (
                    <div className="discount-badge">
                      -{pkg.discountPercent}%
                    </div>
                  )}
                  
                  <div className="package-header">
                    <h3>{pkg.name}</h3>
                    <div className="package-price">
                      {hasPackagePrice(pkg) ? (
                        <>
                          {pkg.discountPercent > 0 && (
                            <span className="original-price">{pkg.price?.toLocaleString()}</span>
                          )}
                          <span className="price-amount">
                            {pkg.discountPercent > 0 
                              ? Math.round(pkg.price * (1 - pkg.discountPercent / 100)).toLocaleString()
                              : pkg.price?.toLocaleString()
                            }
                          </span>
                          <span className="price-currency">CZK</span>
                        </>
                      ) : (
                        <span className="price-depends">Depends on project</span>
                      )}
                    </div>
                  </div>
                  
                  <p className="package-description">{pkg.description}</p>
                  
                  <div className="package-duration">
                    <FiClock />
                    <span>{pkg.duration}</span>
                  </div>

                  <ul className="package-features">
                    {(pkg.features || []).map((feature, idx) => (
                      <li key={idx} className={feature.included ? 'included' : 'not-included'}>
                        {feature.included ? <FiCheck /> : <FiMinus />}
                        <span>{feature.text}</span>
                      </li>
                    ))}
                  </ul>

                  <div className="studio-rental-note">
                    <FiHome />
                    <span>Studio rental: +{pkg.studioRentalPrice?.toLocaleString()} CZK</span>
                  </div>

                  <button 
                    className={`select-package-btn ${selectedPackage?._id === pkg._id ? 'selected' : ''}`}
                    onClick={(e) => {
                      e.stopPropagation();
                      setSelectedPackage(pkg);
                    }}
                  >
                    {selectedPackage?._id === pkg._id ? (
                      <>
                        <FiCheck /> Selected
                      </>
                    ) : (
                      'Select Package'
                    )}
                  </button>
                </div>
              ))}
            </div>

            <div className="info-box">
              <FiInfo />
              <p>
                I always provide online assistance with outfit and accessory selection before the shoot. 
                I usually recommend the 2-hour option, as it allows more time to warm up, 
                work at a comfortable pace, and create more varied results.
              </p>
            </div>

            <div id="step-actions" className="step-actions">
              <button 
                className="next-btn" 
                disabled={!selectedPackage}
                onClick={() => goToStep(2)}
              >
                Continue to Customize
              </button>
            </div>
          </div>
        )}

        {/* Step 2: Customize with Add-ons */}
        {step === 2 && (
          <div className="step-content">
            <h2>Customize Your Session</h2>
            <p className="step-description">Add extras to enhance your experience</p>

            {/* Studio Rental Option */}
            <div className="customize-section">
              <h3>Studio Rental</h3>
              <div 
                className={`studio-option ${includeStudio ? 'selected' : ''}`}
                onClick={() => setIncludeStudio(!includeStudio)}
              >
                <div className="studio-option-content">
                  <div className="studio-icon">
                    <FiHome />
                  </div>
                  <div className="studio-info">
                    <h4>Include Studio Rental</h4>
                    <p>Professional photography studio with lighting equipment and backdrops</p>
                  </div>
                </div>
                <div className="studio-price-toggle">
                  <span className="addon-price">+{selectedPackage?.studioRentalPrice.toLocaleString()} CZK</span>
                  <div className={`toggle ${includeStudio ? 'active' : ''}`}>
                    <div className="toggle-handle"></div>
                  </div>
                </div>
              </div>
            </div>

            {/* Additional Services */}
            <div className="customize-section">
              <h3>Additional Services</h3>
              <div className="addons-grid">
                {bookingAddons.map(addon => (
                  <div
                    key={addon._id}
                    className={`addon-card ${selectedAddons.includes(addon._id) ? 'selected' : ''}`}
                    onClick={() => toggleAddon(addon._id)}
                  >
                    <div className="addon-icon">{addon.icon}</div>
                    <div className="addon-info">
                      <h4>{addon.name}</h4>
                      <p>{addon.description}</p>
                    </div>
                    <div className="addon-action">
                      <span className="addon-price">+{addon.price?.toLocaleString()} CZK</span>
                      <div className={`checkbox ${selectedAddons.includes(addon._id) ? 'checked' : ''}`}>
                        {selectedAddons.includes(addon._id) && <FiCheck />}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* Price Calculator */}
            <div className="price-calculator">
              <h3>Price Summary</h3>
              <div className="price-breakdown">
                {priceBreakdown.map((item, idx) => (
                  <div key={idx} className="breakdown-item">
                    <span>{item.label}</span>
                    <span>{item.dependsOnProject ? 'Depends on project' : `${item.price.toLocaleString()} CZK`}</span>
                  </div>
                ))}
                <div className="breakdown-total">
                  <span>Total</span>
                  <span>{!hasPackagePrice(selectedPackage) && totalPrice === 0 ? 'Depends on project' : `${totalPrice.toLocaleString()} CZK`}</span>
                </div>
              </div>
            </div>

            <div id="step-actions" className="step-actions">
              <button className="back-btn" onClick={() => goToStep(1)}>Back</button>
              <button 
                className="next-btn" 
                onClick={() => goToStep(3)}
              >
                Continue to Date & Time
              </button>
            </div>
          </div>
        )}

        {/* Step 3: Select Date & Time */}
        {step === 3 && (
          <div className="step-content">
            <h2>Select Date & Time</h2>
            <p className="step-description">Choose when you'd like to have your session</p>
            
            <div className="datetime-grid">
              <div className="calendar-section">
                {renderCalendar()}
              </div>
              <div className="time-section">
                <h3>Available Times</h3>
                {selectedDate ? (
                  loadingSlots ? (
                    <p className="select-date-msg">Loading available times...</p>
                  ) : (
                    <>
                      <div className="time-slots">
                        {timeSlots.map(time => {
                          const isBooked = isTimeSlotBooked(time);
                          return (
                            <button
                              key={time}
                              className={`time-slot ${selectedTime === time ? 'selected' : ''} ${isBooked ? 'booked' : ''}`}
                              onClick={() => !isBooked && setSelectedTime(time)}
                              disabled={isBooked}
                              title={isBooked ? 'This time slot is already booked' : `Select ${time}`}
                            >
                              {time}
                              {isBooked && <span className="booked-label">Booked</span>}
                            </button>
                          );
                        })}
                      </div>
                      {bookedSlots.length > 0 && (
                        <p className="booked-slots-info">
                          <FiInfo /> Grayed out times are already booked
                        </p>
                      )}
                    </>
                  )
                ) : (
                  <p className="select-date-msg">Please select a date first</p>
                )}
              </div>
            </div>
            <div id="step-actions" className="step-actions">
              <button className="back-btn" onClick={() => goToStep(2)}>Back</button>
              <button 
                className="next-btn" 
                disabled={!selectedDate || !selectedTime}
                onClick={() => goToStep(4)}
              >
                Continue
              </button>
            </div>
          </div>
        )}

        {/* Step 4: Contact Details */}
        {step === 4 && (
          <div className="step-content">
            <h2>Your Details</h2>
            <p className="step-description">Tell me a bit about yourself and your vision</p>

            <div className="final-step-layout">
              <div className="booking-summary-detailed">
                <h3>Booking Summary</h3>
                
                <div className="summary-section">
                  <div className="summary-item">
                    <span className="summary-label">Package</span>
                    <span className="summary-value">{selectedPackage?.name}</span>
                  </div>
                  <div className="summary-item">
                    <span className="summary-label">Duration</span>
                    <span className="summary-value">{selectedPackage?.duration}</span>
                  </div>
                  <div className="summary-item">
                    <span className="summary-label">Date</span>
                    <span className="summary-value">
                      {selectedDate?.toLocaleDateString('en-US', { 
                        weekday: 'long', 
                        year: 'numeric', 
                        month: 'long', 
                        day: 'numeric' 
                      })}
                    </span>
                  </div>
                  <div className="summary-item">
                    <span className="summary-label">Time</span>
                    <span className="summary-value">{selectedTime}</span>
                  </div>
                </div>

                {(includeStudio || selectedAddons.length > 0) && (
                  <div className="summary-section">
                    <h4>Selected Extras</h4>
                    {includeStudio && (
                      <div className="summary-extra">
                        <FiHome />
                        <span>Studio Rental</span>
                        <span>+{selectedPackage?.studioRentalPrice?.toLocaleString()} CZK</span>
                      </div>
                    )}
                    {selectedAddons.map(addonId => {
                      const addon = bookingAddons.find(a => a._id === addonId);
                      return addon ? (
                        <div key={addon._id} className="summary-extra">
                          <span>{addon.icon}</span>
                          <span>{addon.name}</span>
                          <span>+{addon.price?.toLocaleString()} CZK</span>
                        </div>
                      ) : null;
                    })}
                  </div>
                )}

                <div className="summary-total-section">
                  <div className="summary-total">
                    <span>Total Price</span>
                    <span className="total-amount">{!hasPackagePrice(selectedPackage) && totalPrice === 0 ? 'Depends on project' : `${totalPrice.toLocaleString()} CZK`}</span>
                  </div>
                </div>
              </div>

              <form className="contact-form" onSubmit={handleSubmit}>
                <div className="form-group">
                  <label><FiUser /> Full Name</label>
                  <input
                    type="text"
                    name="name"
                    value={formData.name}
                    onChange={handleFormChange}
                    required
                    placeholder="Your name"
                  />
                </div>
                <div className="form-group">
                  <label><FiMail /> Email</label>
                  <input
                    type="email"
                    name="email"
                    value={formData.email}
                    onChange={handleFormChange}
                    required
                    placeholder="your@email.com"
                  />
                </div>
                <div className="form-group">
                  <label><FiPhone /> Phone</label>
                  <input
                    type="tel"
                    name="phone"
                    value={formData.phone}
                    onChange={handleFormChange}
                    required
                    placeholder="+420 123 456 789"
                  />
                </div>
                <div className="form-group">
                  <label>Message (optional)</label>
                  <textarea
                    name="message"
                    value={formData.message}
                    onChange={handleFormChange}
                    rows="4"
                    placeholder="Tell me about your vision, outfit ideas, or any questions..."
                  ></textarea>
                </div>
                <div id="step-actions" className="step-actions">
                  <button type="button" className="back-btn" onClick={() => goToStep(3)}>Back</button>
                  <button type="submit" className="submit-btn" disabled={submitting}>
                    {submitting ? 'Submitting...' : `Confirm Booking • ${!hasPackagePrice(selectedPackage) && totalPrice === 0 ? 'Depends on project' : `${totalPrice.toLocaleString()} CZK`}`}
                  </button>
                </div>
              </form>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default Booking;
